import React, { useState, useEffect, useMemo } from "react";

import {
  useNavigate,
  useLocation,
  Link
} from "react-router-dom";

import {
  FaChartBar,
  FaClipboardCheck,
  FaUser,
  FaExclamationTriangle,
  FaUserShield,
  FaCogs,
  FaFileAlt,
  FaClock,
  FaProjectDiagram,
  FaHistory,
  FaBell
} from "react-icons/fa";

import logo from "../../images/Logo.png";

/* =========================================================
   ROUTES
========================================================= */

const SUPPORT_ROUTES = {
  DASHBOARD: "/admin/support/dashboard",
  ALL_TICKETS: "/admin/support",
  MY_TICKETS: "/admin/support/my",
  ESCALATED: "/admin/support/escalated",
  HISTORY: "/admin/support/history",
  NOTIFICATIONS: "/admin/support/notifications",
  SLA: "/admin/support/sla",
  AUTOMATION: "/admin/support/automation",
  ROUTING: "/admin/support/routing",
  PERFORMANCE: "/admin/support/performance",
  REPORTS: "/admin/support/reports",
  LOGS: "/admin/support/logs",
  SETTINGS: "/admin/support/settings"
};

/* =========================================================
   COMPONENT
========================================================= */

export default function SupportSidebar() {

  const [collapsed, setCollapsed] =
    useState(false);

  const [adminRole, setAdminRole] =
    useState("");

  const navigate = useNavigate();

  const location = useLocation();

  /* =======================================================
     LOAD ROLE
  ======================================================= */

  useEffect(() => {

    const loadRole = () => {

      try {

        const rawRole =
          localStorage.getItem("admin_role") || "";

        const normalizedRole = rawRole
          .trim()
          .replace(/\s+/g, "_")
          .toUpperCase();

        setAdminRole(normalizedRole);

      } catch (error) {

        console.error(
          "Failed to load admin role",
          error
        );

        setAdminRole("");
      }
    };

    loadRole();

    window.addEventListener(
      "storage",
      loadRole
    );

    return () => {

      window.removeEventListener(
        "storage",
        loadRole
      );
    };

  }, []);

  /* =======================================================
     ROLE HELPERS
  ======================================================= */

  const hasRole = (...roles) =>
    roles.includes(adminRole);

  const isSuperAdmin =
    hasRole("SUPER_ADMIN");

  const isSupportAdmin =
    hasRole("SUPPORT_ADMIN");

  const isOpsAdmin =
    hasRole("OPS_ADMIN");

  const isReadOnlyAdmin =
    hasRole("READ_ONLY");

  const isSupportTeam =
    hasRole(
      "SUPER_ADMIN",
      "SUPPORT_ADMIN",
      "OPS_ADMIN"
    );

  /* =======================================================
     MENU STRUCTURE
  ======================================================= */

  const menuSections = useMemo(() => [

    /* =====================================================
       CORE
    ===================================================== */

    {
      title: "CORE",

      items: [
        {
          key: "dashboard",
          label: "Dashboard",
          icon: <FaChartBar />,
          route: SUPPORT_ROUTES.DASHBOARD
        }
      ]
    },

    /* =====================================================
       TICKETS
    ===================================================== */

    {
      title: "TICKETS",

      items: [

        /* ================================================
           SUPER ADMIN
        ================================================ */

        ...(isSuperAdmin
          ? [
              {
                key: "allTickets",
                label: "All Tickets",
                icon: <FaClipboardCheck />,
                route: SUPPORT_ROUTES.ALL_TICKETS
              }
            ]
          : []),

        /* ================================================
           SUPPORT / OPS
        ================================================ */

        ...(isSupportAdmin || isOpsAdmin
          ? [
              {
                key: "myTickets",
                label: "My Tickets",
                icon: <FaUser />,
                route: SUPPORT_ROUTES.MY_TICKETS
              }
            ]
          : []),

        /* ================================================
           ALL SUPPORT USERS
        ================================================ */

...(isOpsAdmin || isSuperAdmin
  ? [
      {
        key: "escalated",
        label: "Escalated",
        icon: <FaExclamationTriangle />,
        route: SUPPORT_ROUTES.ESCALATED
      }
    ]
  : []),

...(isSupportTeam
  ? [
      {
        key: "history",
        label: "Ticket History",
        icon: <FaHistory />,
        route: SUPPORT_ROUTES.HISTORY
      }
    ]
  : [])
      ]
    },

    /* =====================================================
       WORKSPACE
    ===================================================== */

    {
      title: "WORKSPACE",

      items: [

        ...(isSupportAdmin
          ? [
              {
                key: "notifications",
                label: "Notifications",
                icon: <FaBell />,
                route: SUPPORT_ROUTES.NOTIFICATIONS
              }
            ]
          : [])
      ]
    },

    /* =====================================================
       OPERATIONS
    ===================================================== */

    {
      title: "OPERATIONS",

      items: isSuperAdmin
        ? [
            {
              key: "sla",
              label: "SLA Monitor",
              icon: <FaClock />,
              route: SUPPORT_ROUTES.SLA
            },

            {
              key: "automation",
              label: "Automation Rules",
              icon: <FaCogs />,
              route: SUPPORT_ROUTES.AUTOMATION
            },

            {
              key: "routing",
              label: "Routing",
              icon: <FaProjectDiagram />,
              route: SUPPORT_ROUTES.ROUTING
            }
          ]
        : []
    },

    /* =====================================================
       ANALYTICS
    ===================================================== */

    {
      title: "ANALYTICS",

      items: [

        ...(isSuperAdmin
          ? [
              {
                key: "performance",
                label: "Admin Performance",
                icon: <FaUserShield />,
                route: SUPPORT_ROUTES.PERFORMANCE
              }
            ]
          : []),

        ...(isSupportTeam || isReadOnlyAdmin
          ? [
              {
                key: "reports",
                label: "Reports",
                icon: <FaChartBar />,
                route: SUPPORT_ROUTES.REPORTS
              }
            ]
          : [])
      ]
    },

    /* =====================================================
       SYSTEM
    ===================================================== */

    {
      title: "SYSTEM",

      items: isSuperAdmin
        ? [
            {
              key: "logs",
              label: "System Logs",
              icon: <FaFileAlt />,
              route: SUPPORT_ROUTES.LOGS
            },

            {
              key: "settings",
              label: "Settings",
              icon: <FaCogs />,
              route: SUPPORT_ROUTES.SETTINGS
            }
          ]
        : []
    }

  ], [
    adminRole
  ]);

  /* =======================================================
     ACTIVE ROUTE
  ======================================================= */

  const isActiveRoute = (route) => {

    if (route === SUPPORT_ROUTES.ALL_TICKETS) {

      return (
        location.pathname === route
      );
    }

    return location.pathname.startsWith(route);
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <aside
      className={`dashboard_sidebar support_sidebar ${
        collapsed ? "collapsed" : ""
      }`}
    >

      {/* ===================================================
         LOGO
      =================================================== */}

      <div className="sidebar_logo">

        <Link to={SUPPORT_ROUTES.DASHBOARD}>

          <img
            src={logo}
            alt="Mahal"
          />

        </Link>

        <button
          className="collapse_btn"
          onClick={() =>
            setCollapsed(!collapsed)
          }
        >
          <i className="fas fa-bars"></i>
        </button>

      </div>

      {/* ===================================================
         MENU
      =================================================== */}

      <ul className="sidebar_menu">

        {menuSections.map((section) => {

          const items = section.items;

          if (!items?.length) {
            return null;
          }

          return (

            <React.Fragment
              key={section.title}
            >

              {!collapsed && (

                <p className="sidebar_section_title">
                  {section.title}
                </p>

              )}

              {items.map((item) => (

                <li
                  key={item.key}
                  className={`menu_link ${
                    isActiveRoute(item.route)
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    navigate(item.route)
                  }
                  style={{
                    cursor: "pointer"
                  }}
                >

                  {item.icon}

                  {!collapsed && (
                    <span>
                      {item.label}
                    </span>
                  )}

                </li>

              ))}

            </React.Fragment>
          );
        })}

      </ul>

    </aside>
  );
}